'use client'

import Link from 'next/link'
import { useState } from 'react'

import DoctorProfile from '@/components/etc/DoctorProfile'
import Specialties from '@/components/landing-home/Specialties'
import { ClinicDetail } from '@/lib/types/clinic'
import ClinicSectionTabs from './Clinicsectiontabs'

interface ClinicTabContentProps {
  data: ClinicDetail
}

function formatMobileTime(time?: string | null) {
  if (!time) {
    return '-'
  }

  const match = time.match(/^(\d{1,2}):(\d{2})/)
  if (!match) {
    return time
  }

  const hours = Number(match[1])
  if (Number.isNaN(hours)) {
    return time
  }

  const displayHours = hours % 12 || 12
  const period = hours >= 12 ? 'PM' : 'AM'
  return `${displayHours}:${match[2]} ${period}`
}

export default function ClinicTabContent({ data }: ClinicTabContentProps) {
  const [activeTab, setActiveTab] = useState('about')

  return (
    <>
      <ClinicSectionTabs activeTab={activeTab} onTabChange={setActiveTab} />

      <div className='flex flex-col gap-6'>
        {/* About Tab */}
        {activeTab === 'about' && (
          <div>
            <p className='whitespace-pre-wrap text-base font-normal '>{data?.about}</p>
          </div>
        )}

        {/* Specialties Tab */}
        {activeTab === 'specialties' && (
          <div>
            <Specialties specialties={data?.specialties_display} />
          </div>
        )}

        {/* Prescribers Tab */}
        {activeTab === 'prescribers' && (
          <div>
            <div className='flex justify-end mb-5'>
              <Link
                href={`/clinic/prescriberslisting?clinic=${data.id}`}
                className='text-base font-light text-ui-primary-dark'
              >
                View all
              </Link>
            </div>
            {data?.prescribers?.length > 0 ? (
              <div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
                {data.prescribers.map((prescriber) => (
                  <DoctorProfile
                    clinicName={data.name}
                    doctorPhoto={prescriber.profile_picture_display ?? '/images/doctor.jpg'}
                    doctorName={prescriber.name}
                    specialization={prescriber.specialty_display}
                    experience={`${prescriber.total_experience}`}
                    nextSlot={prescriber.next_available_slot ? new Date(prescriber.next_available_slot) : undefined}
                    key={prescriber.name}
                    {...prescriber}
                    buttonLabel='Book Online'
                    buttonRoute={`/clinic/doctor/${prescriber.id}`}
                    buttonClass='flex h-11 w-3/5 items-center justify-center rounded-md  bg-[#F59405] text-center text-base font-semibold text-white hover:bg-black hover:text-white'
                  />
                ))}
              </div>
            ) : (
              <div className='w-full text-center font-medium'>No Prescribers Found</div>
            )}
          </div>
        )}

        {/* Clinic Hours Tab */}
        {activeTab === 'clinic-hours' && (
          <>
            <div className='grid grid-cols-[1fr_1fr_1fr] gap-x-7 gap-y-4 px-1 text-base md:hidden'>
              <p className='font-normal text-black'>Days</p>
              <p className='font-normal text-black'>Start Time</p>
              <p className='font-normal text-black'>End Time</p>

              {data?.timings.map((time) => (
                <div className='contents' key={time.id}>
                  <p className='font-light text-[#4A4A4A]'>{time.day_of_week_display}</p>
                  <p className='font-light text-[#4A4A4A]'>
                    {time.is_working ? formatMobileTime(time.opening_time) : '-'}
                  </p>
                  <p className='font-light text-[#4A4A4A]'>
                    {time.is_working ? formatMobileTime(time.closing_time) : '-'}
                  </p>
                </div>
              ))}
            </div>

            <div className='hidden w-full overflow-x-auto md:block'>
              <div className='px-5'>
                {/* Days Row */}
                <div className='mb-3 grid grid-cols-8 gap-4'>
                  <p className='text-base font-normal'>Days</p>
                  {data?.timings.map((time) => (
                    <p key={time.id} className='text-center text-base font-normal'>
                      {time.day_of_week_display}
                    </p>
                  ))}
                </div>

                {/* Start Time Row */}
                <div className='mb-3 grid grid-cols-8 gap-4'>
                  <p className='text-base font-normal'>Start Time</p>
                  {data?.timings.map((time) => (
                    <p key={time.id} className='text-center text-base font-semibold'>
                      {time.is_working ? time.opening_time : '-'}
                    </p>
                  ))}
                </div>

                {/* End Time Row */}
                <div className='grid grid-cols-8 gap-4'>
                  <p className='text-base font-normal'>End Time</p>
                  {data?.timings.map((time) => (
                    <p key={time.id} className='text-center text-base font-semibold'>
                      {time.is_working ? time.closing_time : '-'}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
